// src/components/Town/utils/revenueTableUtils.js

import {
  applyRowOrder,
  SECTION_ORDER_KEY,
  TOWN_SECTION_ORDER,
  DEBT_SECTION_ORDER,
} from "../../../shared/constants/tableOrder.js";

function sortSectionsByName(sections, orderList) {
  if (!orderList) return sections;

  const copy = [...sections];
  copy.sort((a, b) => {
    const ia = orderList.indexOf(a.name);
    const ib = orderList.indexOf(b.name);

    // Anything not in the list gets pushed to the end
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;

    return ia - ib;
  });

  return copy;
}

/**
 * Add "increase" and "% change" to a row based on y1_adopted vs y0_adopted.
 */
export function addIncreaseFields(row) {
  const prev = row.y1_adopted;
  const curr = row.y0_adopted;

  const withIncrease = { ...row };

  if (typeof prev === "number" && typeof curr === "number") {
    withIncrease.increase = curr - prev;
    withIncrease.percentIncrease =
      prev !== 0 ? ((curr - prev) / prev) * 100 : null;
  } else {
    withIncrease.increase = null;
    withIncrease.percentIncrease = null;
  }

  return withIncrease;
}

/**
 * Build a TOTAL row by summing each numeric column over the provided rows.
 */
export function buildTotalRow(rows, columns, label) {
  const total = { label };

  columns.forEach((col) => {
    if (col.key === "label") return;

    total[col.key] = rows.reduce((sum, row) => {
      const val = row[col.key];
      return typeof val === "number" ? sum + val : sum;
    }, 0);
  });

  return addIncreaseFields(total);
}

/**
 * Apply TABLE_ROW_ORDER to each section's rows.
 * For intergovernmental, we map each subsection name to an order key.
 */
export function applySectionRowOrder(sections, categoryKey) {
  // First, enforce section *order* (top-to-bottom)
  let orderedSections = sections;

  if (categoryKey === "town") {
    orderedSections = sortSectionsByName(sections, TOWN_SECTION_ORDER);
  } else if (categoryKey === "debtServiceAndTransfers") {
    orderedSections = sortSectionsByName(sections, DEBT_SECTION_ORDER);
  }

  // Then apply row order within each section, as before
  return orderedSections.map((section) => {
    const compositeKey = `${categoryKey}:${section.name}`;

    const rowOrderKey =
      SECTION_ORDER_KEY[compositeKey] ||
      SECTION_ORDER_KEY[section.name] ||
      categoryKey;

    return {
      ...section,
      rows: applyRowOrder(section.rows, rowOrderKey),
    };
  });
}

/**
 * Factory for onChange handlers that edit values inside a section list.
 * Used by TAXES, LICENSES, INTERGOV, CHARGES, OTHER, TRANSFERS.
 */
export function createSectionChangeHandler(setSections) {
  return (e, rowLabel, colKey) => {
    const rawValue = e.target.value;
    const updatedValue =
      rawValue === "" ? "" : Number(rawValue.replace(/,/g, ""));

    setSections((prev) =>
      prev.map((section) => ({
        ...section,
        rows: section.rows.map((row) =>
          row.label === rowLabel
            ? { ...row, [colKey]: updatedValue }
            : row
        ),
      }))
    );
  };
}

/**
 * Build display rows for categories that look like:
 *   SECTION HEADER
 *   item rows...
 *   TOTAL [LABEL]
 *
 * Used for: TAXES, LICENSES & PERMITS, CHARGES, OTHER, TRANSFERS.
 */
export function buildSingleSectionDisplayData(sections, columns, totalLabel) {
  if (!sections || !sections.length) return [];

  const rows = [];
  const allItemRows = [];

  sections.forEach((section) => {
    rows.push({ section: section.name });

    section.rows.forEach((row) => {
      const rowWithInc = addIncreaseFields(row);
      rows.push(rowWithInc);
      allItemRows.push(rowWithInc);
    });
  });

  const totalRow = buildTotalRow(allItemRows, columns, totalLabel);
  totalRow.order = 9999;
  rows.push(totalRow);

  return rows;
}

/**
 * Build display rows for INTERGOVERNMENTAL, which has:
 *   SECTION HEADER
 *   item rows...
 *   subsection subtotal (blank label)
 *   ...
 *   TOTAL INTERGOVERNMENTAL
 */
export function buildIntergovDisplayData(sections, columns) {
  if (!sections || !sections.length) return [];

  const rows = [];
  const allItemRows = [];

  sections.forEach((section) => {
    rows.push({ section: section.name });

    const sectionItemRows = section.rows.map(addIncreaseFields);
    sectionItemRows.forEach((r) => {
      rows.push(r);
      allItemRows.push(r);
    });

    // subsection subtotal (blank label line with numbers only)
    const subtotal = buildTotalRow(sectionItemRows, columns, "");
    rows.push(subtotal);
  });

  const grandTotal = buildTotalRow(
    allItemRows,
    columns,
    "TOTAL INTERGOVERNMENTAL"
  );
  grandTotal.order = 9999;
  rows.push(grandTotal);

  return rows;
}
